import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativasEtapa3 } from 'services/iniciativa.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { Header } from '../HomePublic/components/Header';
import { HeroIniciativas } from '../Iniciativas/components/HeroIniciativas';
import { FiltrosIniciativas } from '../Iniciativas/components/FiltrosIniciativas';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import '../Iniciativas/style.scss';
import './style.scss';

const resolveIndicadosFromTarget = (
  ini: IniciativaFormatada
): Array<{ posicao: number; nome: string }> => {
  const chaves = ini.iniciativaOriginal?.indicados ?? [];
  const target = ini.iniciativaOriginal?.target ?? [];
  const byId = new Map(target.map((t) => [t._id, t]));

  return chaves.map((chave, idx) => {
    const member = byId.get(chave);
    const nomeBase =
      member?.extra?.nome_guerra ||
      member?.extra?.nome ||
      member?.name ||
      '';
    const prefixo = member?.extra?.prefixo ? String(member.extra.prefixo) : '-';
    const nome = nomeBase
      ? `${nomeBase} (${chave} - ${prefixo})`
      : `${chave} - ${prefixo}`;
    return { posicao: idx + 1, nome };
  });
};

export function Indicacoes() {
  const { user } = useAuth();
  const location = useLocation();
  const categoriaInicial =
    (location.state as { categoria?: string } | null)?.categoria ?? 'todos';
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [topPorIniciativa, setTopPorIniciativa] = useState<
    Map<string, Array<{ posicao: number; nome: string }>>
  >(new Map());
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>(categoriaInicial);
  const [termoBusca, setTermoBusca] = useState('');
  const [debouncedTermo, setDebouncedTermo] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedTermo(termoBusca.trim());
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [termoBusca]);

  useEffect(() => {
    let cancelled = false;
    if (!user?._id) return;

    async function carregar() {
      try {
        setLoading(true);
        setErro(null);
        const items = await buscarIniciativasEtapa3(user!._id);
        if (cancelled) return;

        const next = new Map<string, Array<{ posicao: number; nome: string }>>();
        items.forEach((ini) => {
          next.set(ini.id, resolveIndicadosFromTarget(ini));
        });

        setIniciativas(items);
        setTopPorIniciativa(next);
      } catch (error) {
        console.error('[Indicacoes] erro ao carregar', error);
        if (cancelled) return;
        setIniciativas([]);
        setTopPorIniciativa(new Map());
        setErro('Não foi possível carregar as iniciativas. Tente novamente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    carregar();
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  const iniciativasFiltradas = useMemo(() => {
    const termo = debouncedTermo.toLowerCase();
    return iniciativas.filter((i) => {
      const okCategoria =
        categoriaSelecionada === 'todos' ||
        i.categoryPremio === categoriaSelecionada;
      const okBusca =
        termo.length === 0 || i.titulo.toLowerCase().includes(termo);
      return okCategoria && okBusca;
    });
  }, [iniciativas, categoriaSelecionada, debouncedTermo]);

  const handleBuscar = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDebouncedTermo(termoBusca.trim());
  };

  return (
    <div id="iniciativas-wrapper">
      <Header />

      <HeroIniciativas
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
        title="Indicações"
        subtitle={
          <>
            Conheça os colaboradores mais indicados <br />
            nas iniciativas finalistas do Prêmio BB 2026
          </>
        }
      />

      <FiltrosIniciativas
        categoriaSelecionada=""
        onCategoriaChange={() => {}}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={handleBuscar}
      />

      <main className="iniciativas-main">
        <div className="iniciativas-main__container">
          <div className="iniciativas-main__header">
            <h2 className="iniciativas-main__count" tabIndex={0} aria-live="polite">
              {iniciativasFiltradas.length} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando iniciativas...</span>
            </div>
          ) : erro ? (
            <div className="iniciativas-main__empty" role="alert">
              <p className="iniciativas-main__empty-title">{erro}</p>
            </div>
          ) : iniciativasFiltradas.length === 0 ? (
            <div className="iniciativas-main__empty" role="status" aria-live="polite">
              <p className="iniciativas-main__empty-title">
                Nenhuma iniciativa encontrada.
              </p>
            </div>
          ) : (
            <div className="iniciativas-main__grid iniciativas-main__grid--indicados">
              {iniciativasFiltradas.map((ini) => (
                <IniciativaCard
                  key={ini.id}
                  {...ini}
                  topIndicados={topPorIniciativa.get(ini.id) ?? []}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
